import { useAuth } from "@/context/auth/AuthContext";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import * as AF from "@/components/form-items/AbstractForm";
import useImageAttachment from "@/hooks/useImageAttachment";
import { generateImageName } from "@/lib/utils";
import { CroppedImageInput } from "../form-fields/CroppedImageInput";

interface CatalogueRaisonneFormProps {
  formBase: string;
}

export const CatalogueRaisonneGeneralForm = ({
  formBase,
}: CatalogueRaisonneFormProps) => {
  const { t } = useTranslation();
  const { instanceData } = useAuth();

  const splitted = useMemo(() => formBase.split("."), [formBase]);

  const imagePosition = useMemo(
    () => `CRGeneral${splitted[splitted.length - 1].toLocaleUpperCase()}`,
    [splitted],
  );

  const { attachment, imageURL, refetch } = useImageAttachment(
    imagePosition,
    instanceData?.id,
  );

  return (
    <AF.Col>
      <AF.Text
        name={`${formBase}.pageName`}
        label={t("cms.catalogueRaisonne.general.form.pageName")}
      />
      <CroppedImageInput
        imageURL={imageURL}
        attachment={attachment}
        refetch={refetch}
        imagePosition={imagePosition}
        aspect={0.78}
        label={t("cms.catalogueRaisonne.general.form.picture")}
        buttonLabel={t("changePhoto")}
        title={t("addImage")}
        buildImageName={(instanceName) =>
          generateImageName({
            primaryType: "CMSPages",
            instanceName,
            translatedPageName: t("seo.imageName.catalogueRaisonne"),
          })
        }
      />
      <AF.RichText
        name={`${formBase}.content`}
        label={t("cms.catalogueRaisonne.general.form.content")}
      />
      <AF.Text
        name={`${formBase}.artworkCTA`}
        label={t("cms.catalogueRaisonne.general.form.artworkCTA")}
      />
      <AF.Text
        name={`${formBase}.multipleCTA`}
        label={t("cms.catalogueRaisonne.general.form.multipleCTA")}
      />
      <AF.Switch
        name={`${formBase}.showPublicAuctions`}
        label={t("cms.catalogueRaisonne.general.form.showPublicAuctions")}
      />
    </AF.Col>
  );
};

export const CatalogueRaisonneSpecificForm = ({
  formBase,
  positionBase,
}: CatalogueRaisonneFormProps & { positionBase: string }) => {
  const { t } = useTranslation();
  const { instanceData } = useAuth();

  const splitted = formBase.split(".");

  const imagePosition = useMemo(
    () => `${positionBase}${splitted[splitted.length - 1].toLocaleUpperCase()}`,
    [splitted, positionBase],
  );

  const { attachment, imageURL, refetch } = useImageAttachment(
    imagePosition,
    instanceData?.id,
  );

  return (
    <AF.Col>
      <AF.Text
        name={`${formBase}.pageName`}
        label={t("cms.catalogueRaisonne.specific.form.pageName")}
      />
      <CroppedImageInput
        imageURL={imageURL}
        attachment={attachment}
        refetch={refetch}
        imagePosition={imagePosition}
        aspect={0.78}
        label={t("crAccueilImageLabel")}
        buttonLabel={t("changePhoto")}
        title={t("addImage")}
        buildImageName={(instanceName) =>
          generateImageName({
            primaryType: "CMSPages",
            instanceName,
            translatedPageName: t("seo.imageName.catalogueRaisonne"),
          })
        }
      />
      <AF.Text
        name={`${formBase}.runningTitle`}
        label={t("cms.catalogueRaisonne.specific.form.runningTitle")}
      />
      <AF.RichText
        name={`${formBase}.content`}
        label={t("cms.catalogueRaisonne.specific.form.content")}
      />
    </AF.Col>
  );
};
